import axios from "axios";
import { route } from "ziggy-js";
import { useAuthStore } from "@/js/stores/authStore";

const apiClient = axios.create({
    headers: {
        "Content-Type": "application/json",
    },
    withCredentials: true,
});

// Добавление токена авторизации к запросам
apiClient.interceptors.request.use((config) => {
    const authStore = useAuthStore();
    const token = authStore.authToken;
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});
// Модифікуємо перехватчик відповіді
apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (originalRequest.url.includes("/refresh")) {
            const authStore = useAuthStore();
            authStore.logout();
            window.location.href = "/login";
            return Promise.reject(error);
        }

        if (
            error.response &&
            error.response.status === 401 &&
            !originalRequest._retry
        ) {
            originalRequest._retry = true;
            try {
                const newToken = await refreshToken();
                const authStore = useAuthStore();
                authStore.login(newToken);
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return apiClient(originalRequest);
            } catch (refreshError) {
                const authStore = useAuthStore();
                authStore.logout();
                window.location.href = "/login";
                return Promise.reject(refreshError);
            }
        }
        return Promise.reject(error);
    },
);

/**
 * Logs in a user using their email and password.
 * Sends a POST request to the login route.
 *
 * @param {string} email - The user's email address.
 * @param {string} password - The user's password.
 * @returns {Promise<Object>} The response data from the server.
 * @throws Will throw an error if the network request fails.
 */
export const login = async (email, password) => {
    try {
        const response = await apiClient.post(route("login"), {
            email,
            password,
        });
        return response.data;
    } catch (error) {
        throw error.response ? error.response.data : new Error("Network Error");
    }
};
/**
 * Logs out the current user.
 * Sends a POST request to the logout route.
 *
 * @returns {Promise<Object>} The response data from the server.
 * @throws Will throw an error if the network request fails.
 */
export const logout = async () => {
    try {
        const response = await apiClient.post(route("logout"));
        return response.data;
    } catch (error) {
        throw error.response ? error.response.data : new Error("Network Error");
    }
};
export const refreshToken = async () => {
    try {
        const response = await apiClient.post(
            route("refresh"),
            {},
            {
                withCredentials: true,
            },
        );
        return response.data.data.accessToken;
    } catch (error) {
        throw error.response ? error.response.data : new Error("Network Error");
    }
};

// Загрузка файла
/**
 * Uploads a file to the server.
 * Sends a POST request to the product load route with the file data.
 *
 * @param {File} file - The file to be uploaded.
 * @returns {Promise<Object>} The response data from the server.
 * @throws Will throw an error if the network request fails.
 */
export const loadFile = async (file, brand_id, allowErrors = false) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("brand_id", brand_id);
    formData.append("wrong_skip", allowErrors ? 1 : 0);
    try {
        const response = await apiClient.post(route("product.load"), formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
            withCredentials: true,
        });
        return response.data;
    } catch (error) {
        throw error.response ? error.response.data : new Error("Network Error");
    }
};

export const saveSymlinkApi = async (data) =>{
    try {
        const response = await apiClient.post(route("product.set-symlink"), data);
        return response.data;
    } catch (error) {
        throw error.response ? error.response.data : new Error("Network Error");
    }
}

/**
 * Retrieves a list of products from the server.
 * Sends a GET request to the product index route with pagination, filtering, and sorting parameters.
 *
 * @param {number} [page=1] - The page number for pagination.
 * @param {number} [perPage=10] - The number of products per page.
 * @param {Object|null} [filters=null] - Filtering parameters (e.g., name, barcode, brand_id, price).
 * @param {string|null} [sortBy=null] - The field name to sort by (e.g., 'name', 'price').
 * @param {string|null} [sortOrder=null] - The order direction ('asc' or 'desc').
 * @returns {Promise<Object>} The response data containing the list of products.
 * @throws Will throw an error if the network request fails.
 */
export const getProducts = async (
    page = 1,
    perPage = 10,
    filters = null,
    sortBy = null,
    sortOrder = null,
) => {
    const params = {
        page,
        per_page: perPage,
        filters: { ...(filters ? filters : {}) },
    };
    if (params.filters?.is_sale){
        params.filters.is_sale = params.filters.is_sale ? 1 : 0;
    }
    if (params.filters) {
        for (let i in params.filters) {
            if (!params.filters[i]) {
                delete params.filters[i];
            }
        }
    }


    if (sortBy && sortOrder) {
        params.sort_by = sortBy;
        params.sort_order = sortOrder;
    }
    console.log(params)
    try {
        const response = await apiClient.get(route("product.index"), {
            params,
        });
        return response.data;
    } catch (error) {
        throw error.response ? error.response.data : new Error("Network Error");
    }
};

export const createProduct = async (productData) => {
    try {
        const response = await apiClient.post(
            route("product.store"),
            productData,
        );
        return response.data;
    } catch (error) {
        throw error.response?.data || new Error("Network Error");
    }
};

export const getReport = async (reportData) => {
    try {
        const response = await apiClient.post(
            route("reports.create"),
            reportData,
        );
        return response.data;
    } catch (error) {
        throw error.response?.data || new Error("Network Error");
    }
};

export const updateProduct = async (productId, productData) => {
    try {
        const response = await apiClient.put(
            route("product.update", productId),
            productData,
        );
        return response.data;
    } catch (error) {
        throw error.response?.data || new Error("Network Error");
    }
};

export const getPaymentMethods = async () => {
    try {
        const response = await apiClient.get(route("payment-methods.index"), {
            params: {
                per_page: 99999,
            },
        });
        return response.data;
    } catch (error) {
        throw error.response ? error.response.data : new Error("Network Error");
    }
};
export const getBrands = async () => {
    try {
        const response = await apiClient.get(route("brands.index"), {
            params: {
                per_page: 99999,
            },
        });
        return response.data;
    } catch (error) {
        throw error.response ? error.response.data : new Error("Network Error");
    }
};

export const getSizes = async () => {
    try {
        const response = await apiClient.get(route("sizes.index"), {
            params: {
                per_page: 99999,
            },
        });
        return response.data;
    } catch (error) {
        throw error.response ? error.response.data : new Error("Network Error");
    }
};
export const getColors = async () => {
    try {
        const response = await apiClient.get(route("colors.index"), {
            params: {
                per_page: 99999,
            },
        });
        return response.data;
    } catch (error) {
        throw error.response ? error.response.data : new Error("Network Error");
    }
};

export const searchProduct = async (query, payment_type_code, brand = null) => {
    // http://127.0.0.1:8000/api/find?q=Г2700&payment_type_code=pre-order
    try {
        let searchParams = {
            q: query,
        };
        if (payment_type_code) {
            searchParams.payment_type_code = payment_type_code;
        }
        if (brand) {
            searchParams.brand_id = brand;
        }
        const response = await apiClient.get(route("product.find"), {
            params: searchParams,
        });

        return response;
    } catch (error) {
        throw error.response ? error.response.data : new Error("Network Error");
    }
};
export const createOrder = (orderData) => {
    return apiClient.post(route("orders.store"), orderData);
};
export const getOrderData = (orderId) => {
    return apiClient.get(route("orders.show", orderId));
};

export const getReportFile = (filters) => {
    return apiClient.post(route("order.report"), filters, {
        responseType: "blob",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
    });
};
export const getRemindsFile = (filters) => {
    return apiClient.post(route("product.reminds"), {
        filters:filters
    }, {
        responseType: "blob",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
    });
};
export const getReturnFile = (id) => {
    return apiClient.post(route("product.return-report"), {id}, {
        responseType: "blob",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
    });
};

export const getOrders = async (
    page = 1,
    perPage = 10,
    filters = null,
    sortBy = null,
    sortOrder = null,
) => {
    const params = {
        page,
        per_page: perPage,
        filters: { ...(filters ? filters : {}) },
    };
    if (params.filters) {
        for (let i in params.filters) {
            if (!params.filters[i]) {
                delete params.filters[i];
            }
        }
    }

    if (sortBy && sortOrder) {
        params.sort_by = sortBy;
        params.sort_order = sortOrder;
    }
    try {
        const response = await apiClient.get(route("orders.index"), {
            params,
        });
        return response.data;
    } catch (error) {
        throw error.response ? error.response.data : new Error("Network Error");
    }
};

export const getOrderTypes = async () => {
    try {
        const response = await apiClient.get(route("order-types.index"), {
            params: {
                per_page: 99999,
            },
        });
        return response.data;
    } catch (error) {
        throw error.response ? error.response.data : new Error("Network Error");
    }
};
export const getBrandReturns = async (
    page = 1,
    perPage = 10,
    filters = null,
    sortBy = null,
    sortOrder = null,
) => {
    const params = {
        page,
        per_page: perPage,
        filters: { ...(filters ? filters : {}) },
    };
    if (params.filters) {
        for (let i in params.filters) {
            if (!params.filters[i]) {
                delete params.filters[i];
            }
        }
    }

    if (sortBy && sortOrder) {
        params.sort_by = sortBy;
        params.sort_order = sortOrder;
    }
    try {
        const response = await apiClient.get(route("orders.index"), {
            params,
        });
        return response.data;
    } catch (error) {
        throw error.response ? error.response.data : new Error("Network Error");
    }
};
export const cancelOrder = (orderId) => {
    return apiClient.post(route("orders.cancel"), {
        id: orderId,
    });
};
export const createReturnOrder = (orderData) => {
    return apiClient.post(route("orders.store"), orderData);
};

export const getBrandReturnData = (orderId) => {
    return apiClient.get(route("orders.show", orderId));
};
